// Shared helpers for the new dashboard pages.
import React, {useState, useMemo, useCallback} from 'react';
import {auth} from '../../firebase';

export const NEUGENCE_API_BASE = 'https://app.neugence.com';

export async function bearerHeaders() {
	const user = auth && auth.currentUser;
	if (!user) throw new Error('Not signed in');
	const token = await user.getIdToken();
	return {Authorization: `Bearer ${token}`};
}

export function fmtNumber(n) {
	if (n == null || Number.isNaN(n)) return '—';
	if (typeof n !== 'number') return String(n);
	return n.toLocaleString();
}

export function fmtPct(num, den) {
	if (!den || den === 0) return '—';
	return `${((num / den) * 100).toFixed(1)}%`;
}

export function fmtAgo(tsMs) {
	if (!tsMs) return '—';
	const sec = Math.floor((Date.now() - Number(tsMs)) / 1000);
	if (sec < 60) return `${sec}s ago`;
	if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
	if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
	return `${Math.floor(sec / 86400)}d ago`;
}

export function fmtTimestamp(tsMs) {
	if (!tsMs) return '—';
	try {
		return new Date(Number(tsMs)).toLocaleString();
	} catch (_e) {
		return '—';
	}
}

export const PERIOD_OPTIONS = [
	{key: '1d', label: '1d'},
	{key: '2d', label: '2d'},
	{key: '3d', label: '3d'},
	{key: '7d', label: '7d'},
	{key: '30d', label: '30d'},
];

// ---------------------------------------------------------------------------
// Sortable-table primitives — drop-in hook + header cell for any table in the
// dashboard. Operator asked for sortable columns (open rate, click rate,
// bounce rate, etc.) across every dashboard page.
//
// Usage:
//   const {rows, sortKey, sortDir, onSort} = useSortableTable(data, 'open_rate', 'desc');
//   <thead><tr>
//     <SortHeader sortKey={sortKey} sortDir={sortDir} onSort={onSort} column="name">Name</SortHeader>
//     <SortHeader sortKey={sortKey} sortDir={sortDir} onSort={onSort} column="open_rate" numeric>Open rate</SortHeader>
//   </tr></thead>
//   <tbody>{rows.map(r => …)}</tbody>
//
// Numbers sort numerically. Strings sort lexicographically (case-insensitive).
// Nulls/undefined sort last regardless of direction. Click same header twice
// to flip asc/desc; click another header to switch column with default
// direction (desc for numbers, asc for strings).

function _cmp(a, b) {
	if (a == null && b == null) return 0;
	if (a == null) return 1;
	if (b == null) return -1;
	if (typeof a === 'number' && typeof b === 'number') {
		return a - b;
	}
	const as = String(a).toLowerCase();
	const bs = String(b).toLowerCase();
	if (as < bs) return -1;
	if (as > bs) return 1;
	return 0;
}

export function useSortableTable(rows, defaultKey = null, defaultDir = 'desc') {
	const [sortKey, setSortKey] = useState(defaultKey);
	const [sortDir, setSortDir] = useState(defaultDir);

	const onSort = useCallback((column, numericDefault = false) => {
		setSortKey(prev => {
			if (prev === column) {
				setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
				return prev;
			}
			setSortDir(numericDefault ? 'desc' : 'asc');
			return column;
		});
	}, []);

	const sorted = useMemo(() => {
		if (!Array.isArray(rows)) return rows;
		if (!sortKey) return rows;
		const out = rows.slice();
		out.sort((ra, rb) => {
			const va = ra ? ra[sortKey] : null;
			const vb = rb ? rb[sortKey] : null;
			const c = _cmp(va, vb);
			return sortDir === 'asc' ? c : -c;
		});
		return out;
	}, [rows, sortKey, sortDir]);

	return {rows: sorted, sortKey, sortDir, onSort, setSortKey, setSortDir};
}

export function SortHeader({sortKey, sortDir, onSort, column, numeric, children, className, ...rest}) {
	const active = sortKey === column;
	const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
	const cls = [
		'cursor-pointer select-none whitespace-nowrap',
		active ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900',
		className || '',
	].join(' ');
	return (
		<th
			{...rest}
			className={cls}
			onClick={() => onSort(column, !!numeric)}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSort(column, !!numeric);
				}
			}}
			aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
		>
			{children}
			<span aria-hidden="true" className="text-[10px] text-slate-400 ml-0.5">{arrow}</span>
		</th>
	);
}
